export type BatchFunction<T> = (batch: T[]) => Promise<void>;
export type SetTimeoutFn<T> = (handler: () => void, timeout: number) => T;
export type ClearTimeoutFn<T> = (handle: T) => void;

interface BatchQueueConfig<T, H> {
  batchSize: number;
  timeout?: number;
  executeBatch: BatchFunction<T>;
  setTimeout: SetTimeoutFn<H>;
  clearTimeout: ClearTimeoutFn<H>;
  baseDelay?: number;
  retries?: number;
}

export class BatchQueue<T, H> {
  private queue: T[] = [];
  private readonly batchSize: number;
  private readonly timeout: number;
  private timeoutHandle: H | null = null;
  private readonly executeBatch: BatchFunction<T>;
  private readonly setTimeout: SetTimeoutFn<H>;
  private readonly clearTimeout: ClearTimeoutFn<H>;
  private pending: Promise<void> | null = null;
  private readonly baseDelay: number;
  private readonly retries: number;

  constructor(config: BatchQueueConfig<T, H>) {
    this.batchSize = config.batchSize;
    this.timeout = config.timeout ?? 500;
    this.executeBatch = config.executeBatch;
    this.setTimeout = config.setTimeout;
    this.clearTimeout = config.clearTimeout;
    this.baseDelay = config.baseDelay ?? 500;
    this.retries = config.retries ?? 5;
  }

  public submit(task: T): void {
    this.queue.push(task);

    if (this.queue.length >= this.batchSize) {
      void this.flush(); // Fire and forget
    } else if (this.queue.length === 1) {
      this.startTimer();
    }
  }

  private startTimer(): void {
    this.timeoutHandle = this.setTimeout(() => {
      void this.flush();
    }, this.timeout);
  }

  private clearTimer(): void {
    if (this.timeoutHandle !== null) {
      this.clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }

  public async flush(): Promise<void> {
    this.clearTimer();

    if (this.queue.length === 0) {
      return;
    }

    if (this.pending) {
      return this.pending;
    }

    try {
      this.pending = this.flushInner();
      await this.pending;
    } finally {
      this.pending = null;
    }
  }

  private async flushInner(): Promise<void> {
    while (this.queue.length > 0) {
      const batch = this.queue.slice(0, this.batchSize);
      this.queue = this.queue.slice(this.batchSize);
      await this.executeBatchWithRetry(batch);
    }
  }

  private async executeBatchWithRetry(batch: T[]): Promise<void> {
    await this.retryWithExponentialBackoff(async () => {
      await this.executeBatch(batch);
    });
  }

  private async retryWithExponentialBackoff<R>(
    operation: () => Promise<R>,
  ): Promise<R> {
    const maxRetries = Math.max(0, this.retries);

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt === maxRetries) {
          throw error;
        }

        const delay = this.baseDelay * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw new Error("Failed to execute batch after retries");
  }
}
